<template>
  <div class="workspace-document-status-indicator">
    <progress-steps
      :current-step="currentStep"
      :is-completed="workflowStatus === WorkflowStatus.APPROVED"
      :steps="steps"
      @dropdown-update="handleStatusUpdated"
    />
  </div>
</template>

<script setup lang="ts">
  import { useRoles } from '@/composables/useRoles';
  import { hideAllPoppers } from 'floating-vue';
  import { computed } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { Role, WorkflowStatus } from 'types';
  import ProgressSteps from '../ProgressSteps/ProgressSteps.vue';
  import { Step } from '../ProgressSteps/ProgressSteps.types';
  import WorkspaceDocumentStatusDropdown from './WorkspaceDocumentStatusDropdown.vue';
  import { WorkspaceDocumentStatusDropdownProps } from './WorkspaceDocumentStatusDropdown.types';

  const WORKFLOW_STATUSES = [
    WorkflowStatus.IN_PROGRESS,
    WorkflowStatus.IN_REVIEW,
    WorkflowStatus.APPROVED,
  ] as const;

  const props = defineProps<{
    workspaceId: string;
    documentId: string;
    workflowStatus: WorkflowStatus;
    canEditDocument: boolean;
  }>();

  const emit = defineEmits<{
    (e: 'status-updated'): void;
  }>();

  const { t } = useI18n();
  const { isUserRole } = useRoles();

  const currentStep = computed(() => {
    return WORKFLOW_STATUSES.indexOf(props.workflowStatus);
  });

  const steps = computed((): Step<WorkspaceDocumentStatusDropdownProps>[] => {
    return WORKFLOW_STATUSES.map((status, index) => ({
      label: t(`workflowStatus.${status}`),
      value: index,
      displayValue: index + 1,
      dropdownComponent: getDropdownComponent(status),
    }));
  });

  function getDropdownComponent(
    status: WorkflowStatus,
  ): Step<WorkspaceDocumentStatusDropdownProps>['dropdownComponent'] {
    if (status !== props.workflowStatus) {
      return undefined;
    }

    if (status === WorkflowStatus.IN_PROGRESS && canMoveToInReview()) {
      return {
        component: WorkspaceDocumentStatusDropdown,
        props: {
          workspaceId: props.workspaceId,
          documentId: props.documentId,
          action: 'setInReview',
        },
      };
    }

    if (status === WorkflowStatus.IN_REVIEW && canApprove()) {
      return {
        component: WorkspaceDocumentStatusDropdown,
        props: {
          workspaceId: props.workspaceId,
          documentId: props.documentId,
          action: 'approve',
        },
      };
    }

    return undefined;
  }

  function canMoveToInReview(): boolean {
    return (
      isUserRole([Role.Admin]) ||
      (props.canEditDocument && isUserRole([Role.WorkspaceCoworker]))
    );
  }

  function canApprove(): boolean {
    return isUserRole([Role.Admin, Role.Reviewer]);
  }

  function handleStatusUpdated(): void {
    hideAllPoppers();
    emit('status-updated');
  }
</script>

<style scoped lang="scss">
  .workspace-document-status-indicator {
    width: 100%;
    padding: 0 5%;
  }
</style>
